import supabase, { getImageUrl } from "./supabase";

/**
 * Fetches all projects.
 *
 * @returns {Promise<Array<object>>} The list of projects.
 */
export async function getProjects() {
    const { data, error } = await supabase
        .from("projects")
        .select("*, shows(*)");

    if (error) {
        console.error(error);
        throw new Error("Projects could not be loaded");
    }

    return data.map((project) => ({
        ...project,
        thumbnail: getImageUrl(project.thumbnail_key),
        images: project.image_keys?.map((path) => getImageUrl(path)) ?? [],
        text: project.body ?? [],
        selfHosted: project.self_hosted,
        hasPortraitThumbnail: project.has_portrait_thumbnail,
        videoUrl: project.video_url,
    }));
}

/**
 * Creates a new project or updates an existing one.
 *
 * @param {object} newProject - Project payload to persist.
 * @param {number | string} [id] - Existing project id for edit mode.
 * @returns {Promise<object>} The created or updated project.
 */
export async function createEditProject(newProject, id) {
    // 1. Create/edit project
    let query = supabase.from("projects");

    // A) CREATE
    if (!id) query = query.insert([{ ...newProject }]);

    // B) EDIT
    if (id) query = query.update({ ...newProject }).eq("id", id);

    const { data, error } = await query.select().single();

    if (error) {
        console.error(error);
        throw new Error("Project could not be created");
    }

    return data;
}

/**
 * Deletes a project by id.
 *
 * @param {number | string} id - Project id.
 * @returns {Promise<object | null>} Deleted project response from Supabase.
 */
export async function deleteProject(id) {
    const { data, error } = await supabase
        .from("projects")
        .delete()
        .eq("id", id);

    if (error) {
        console.error(error);
        throw new Error("Project could not be deleted");
    }

    return data;
}
